// Storage persistence + quota helpers (Phase 9a — offline caching hardening).
//
// Mission-critical for an on-the-water app: queued videos/catches live in
// IndexedDB until the device reconnects "at the ramp". Without persistent
// storage the browser may evict that data under storage pressure, silently
// losing a catch. These helpers request durable storage and surface quota.
//
// All functions are best-effort and never throw — they degrade to a safe
// "unknown" result on unsupported platforms (older WebViews, etc.).

/**
 * Ask the browser to make storage persistent so queued offline data is not
 * evicted. Returns the resulting persisted state (or null if unsupported).
 * Safe to call repeatedly; once granted it stays granted.
 */
export async function requestPersistentStorage() {
  try {
    if (!navigator?.storage?.persist) return null;
    // If already persisted, don't re-prompt.
    if (navigator.storage.persisted) {
      const already = await navigator.storage.persisted();
      if (already) return true;
    }
    return await navigator.storage.persist();
  } catch {
    return null;
  }
}

/**
 * Current persisted state without requesting it. Returns true/false, or null
 * if the API is unavailable.
 */
export async function isStoragePersisted() {
  try {
    if (!navigator?.storage?.persisted) return null;
    return await navigator.storage.persisted();
  } catch {
    return null;
  }
}

/**
 * Device storage estimate. Returns { usage, quota, percentUsed, available }
 * in bytes (percentUsed 0–100), or null if unsupported.
 */
export async function getStorageEstimate() {
  try {
    if (!navigator?.storage?.estimate) return null;
    const { usage = 0, quota = 0 } = await navigator.storage.estimate();
    const percentUsed = quota > 0 ? Math.min(100, (usage / quota) * 100) : 0;
    return {
      usage,
      quota,
      available: Math.max(0, quota - usage),
      percentUsed,
    };
  } catch {
    return null;
  }
}

/**
 * Returns true when the device is running low on storage (default threshold
 * 90% used), so the UI can warn before a trip fills the disk mid-session.
 */
export async function isStorageLow(thresholdPercent = 90) {
  const est = await getStorageEstimate();
  if (!est) return false;
  return est.percentUsed >= thresholdPercent;
}
